import { HttpClient } from '@angular/common/http';
import {
    Component,
    ElementRef,
    EventEmitter,
    Inject,
    Input,
    OnDestroy,
    OnInit,
    Output,
    Renderer2,
    ViewChild,
} from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { AuthorizationService } from '@netfoundry-ui/shared/authorization';
import { GrowlerData, GrowlerService } from '@netfoundry-ui/shared/growler';
import { Endpoint, Network, Service } from '@netfoundry-ui/shared/model';
import { ApiService, HTTP_CLIENT, LoggerService } from '@netfoundry-ui/shared/services';
import { FileUploader } from 'ng2-file-upload';
import { FileSaverService } from 'ngx-filesaver';
import { Subscription } from 'rxjs';

@Component({
    selector: 'app-bulk-upload-gateways-form',
    templateUrl: './bulk-upload-gateways-form.component.html',
    styleUrls: ['./bulk-upload-gateways-form.component.scss'],
})
export class BulkUploadGatewaysFormComponent implements OnInit, OnDestroy {
    @ViewChild('fileInput', { static: true }) fileInput: ElementRef;
    @ViewChild('saveGatewaysAsCode', { static: true }) saveGatewaysAsCode;
    @ViewChild('showSummary', { static: true }) showSummary;

    @Input() isInline = false;
    @Input() showBreakoutFields = false;
    @Input() public currentOrgId;
    @Input() public currentNetworkId;
    @Input() hideHaUpgrade = false;
    @Output() back: EventEmitter<boolean> = new EventEmitter();
    @Output() hide: EventEmitter<any> = new EventEmitter();
    @Output() newGateway: EventEmitter<Endpoint> = new EventEmitter();
    step = 0;
    currentNetwork = new Network({});
    networkId;
    isLoading = false;
    // create an empty uploader until we have an actual URL
    uploader: FileUploader;
    fileUpload;
    form: FormGroup;
    fileIsReady = false;
    processing = false;
    hideHelp = false;
    businessKey;
    canListEndpoints = true;
    canCreateEndpoints = true;
    gateways = [];
    hasNewClients = true;
    isAdding = true;
    isEditing = false;
    ipNetworkServices: Service[] = [];
    ipHostServices: Service[] = [];
    sendto: string[] = [];
    sendingState: string[] = [];
    cloneMessage = 'Want to add another environment<br />by uploading another csv file?';
    gatewayClusters = [];
    // boolean to determine if the window is attempting to hide
    private subscription = new Subscription();

    constructor(
        private logger: LoggerService,
        private renderer: Renderer2,
        private formBuilder: FormBuilder,
        private growlerService: GrowlerService,
        private apiService: ApiService,
        @Inject(HTTP_CLIENT) private http: HttpClient,
        private fileSaverService: FileSaverService,
        private dialogRef: MatDialogRef<BulkUploadGatewaysFormComponent>,
        public authorizationService: AuthorizationService,
        public dialogForm: MatDialog
    ) {
        // initialize file upload form
        this.form = this.formBuilder.group({
            networkId: '',
            file: null,
        });

        // initialize the uploader for the drag and drop
        this.uploader = new FileUploader({ url: '' });
        this.canListEndpoints = this.authorizationService.canListEndpoints(undefined, undefined);
        this.canCreateEndpoints = this.authorizationService.canCreateEndpoints(undefined, undefined);
    }

    ngOnInit() {
        this.subscription.add(
            this.apiService.currentNetwork.subscribe((network) => {
                this.currentNetwork = new Network(network);
                this.networkId = this.currentNetwork.getId();

                // upload URL
                const uploadOptions = {
                    url: this.currentNetwork.getSelfLink() + '/endpoints',
                    authToken: 'Bearer ' + localStorage.getItem('access_token'),
                    additionalParameter: {
                        networkId: this.apiService.theNetworkIs.getId(),
                    },
                };
                this.uploader = new FileUploader(uploadOptions);
            })
        );
    }

    showDialog() {
        const event = new MouseEvent('click', { bubbles: true });
        this.fileInput.nativeElement.dispatchEvent(event);
    }

    ngOnDestroy() {
        this.subscription.unsubscribe();
    }

    download() {
        const templateFile = 'GatewaysTemplate.csv';
        this.http
            .get(`/assets/data/${templateFile}`, {
                observe: 'response',
                responseType: 'blob',
            })
            .subscribe((res) => {
                this.fileSaverService.save(res.body, templateFile);
            });
    }

    close() {
        this.dialogRef.close();
    }

    save() {
        this.logger.info('Saving Gateways data...');
        this.addGatewaysCSV();
    }

    async addGatewaysCSV() {
        // if we used the drag and drop
        if (this.uploader.queue.length > 0) {
            this.processing = true;
            const FileObject = this.uploader.queue[0];
            this.logger.info('Temp file', FileObject);
            this.logger.info('Adding file from queue...', FileObject._file);
            await FileObject.upload();
            this.fileUpload = FileObject;
            this.isLoading = false;

            this.step = 1;
            FileObject.onComplete = (response: any, status: any, headers: any) => {
                if (status < 300) {
                    const gatewaysAsCodeResponse = JSON.parse(response);
                    this.businessKey = gatewaysAsCodeResponse['businessKey'];
                    this.gateways = gatewaysAsCodeResponse['endpoints'];
                    this.gatewayClusters = gatewaysAsCodeResponse['gatewayClusters'];

                    if (this.gateways !== undefined && this.gateways.length > 0) {
                        this.newGateway.emit(new Endpoint(this.gateways[0]));
                    }

                    this.saveGatewaysAsCode.fetchStatus();
                    this.growlerService.show(
                        new GrowlerData(
                            'success',
                            'Success',
                            'Gateways Template Upload Complete',
                            'Gateways template upload has been successful.'
                        )
                    );
                } else {
                    const error = JSON.parse(response);
                    this.logger.info('ERROR MESSAGE', error);
                    let errorMessage = '';
                    if (error != null && error[0] != null && error[0]['message'] != null) {
                        errorMessage = error[0]['message'];
                    }

                    this.growlerService.show(
                        new GrowlerData(
                            'error',
                            'Error',
                            'Gateways Template Upload Failed',
                            'Gateways template upload has failed. Import has been canceled. ' + errorMessage
                        )
                    );
                }

                this.processing = false;
                this.uploader.clearQueue();
                if (status >= 300) {
                    this.resetForm();
                }
            };
        } else {
            this.logger.error('No files in queue to upload');
            this.growlerService.show(
                new GrowlerData('error', 'Error', 'Gateways Template Upload Failed', 'No files queued to upload')
            );
        }
    }

    onFileChange(event) {
        if (event.target.files.length > 0) {
            const file = event.target.files[0];
            this.logger.info('Adding file...', file);
            this.form.get('file').setValue(file);
            this.fileIsReady = true;
            this.uploader.addToQueue([this.form.get('file').value]);
        }
    }

    fileDrop() {
        if (this.uploader.queue.length > 0) {
            this.fileIsReady = true;
        }
    }

    // function for resetting the form
    resetForm() {
        this.businessKey = null;
        this.fileIsReady = false;
        this.processing = false;

        this.gateways = [];
        this.hasNewClients = true;
        this.isAdding = true;
        this.isEditing = false;

        this.sendto = [];
        this.sendingState = [];
        this.fileInput.nativeElement.value = '';

        this.step = 0;
    }
}
